/**
 * Head tags for the public site, written synchronously on first render so the
 * crawler pre-render snapshot carries the right title, description and canonical.
 */
export const SITE = "https://ohcbenefits.com";
export const OG_IMAGE = `${SITE}/og-image.jpg`;
export const SITE_NAME = "OneHarvestCo. Benefits";

const HOME_DESCRIPTION =
  "Local, owner-led benefits practice in The Woodlands. Group health plans and PEO options for employers with 10–150 employees, plus individual & family ACA coverage.";

export const SEO_ROUTES = {
  "/": {
    title: "Group Health Plans & PEO Options for Employers | The Woodlands, TX",
    description: HOME_DESCRIPTION,
  },
  "/services": {
    title: "Services: Group Health, PEO & Individual Coverage | OHC Benefits",
    description:
      "Group health plan design and renewals, PEO switches and placements, and individual & family ACA coverage from a licensed Texas agent in The Woodlands.",
  },
  "/employers": {
    title: "Group Health Insurance & PEO Broker for Employers | The Woodlands, TX",
    description:
      "Group health, ancillary and ICHRA options, Jan 1 renewal reviews and PEO switches for employers with 10–150 employees in The Woodlands, Houston and surrounding communities.",
  },
  "/individuals": {
    title: "Individual & Family Health Insurance (ACA) | The Woodlands, TX",
    description:
      "Help choosing ACA marketplace and private health plans in The Woodlands, Houston and surrounding communities. Open Enrollment runs Nov 1–Jan 15. Carriers pay my commission.",
  },
  "/about": {
    title: "About Kyle Walker, Licensed Texas Health Insurance Agent | OHC Benefits",
    description:
      "Born and raised in The Woodlands, Kyle Walker is a licensed Texas health insurance agent and the owner of OneHarvestCo. Benefits. You work with him directly.",
  },
  "/education": {
    title: "Health Insurance & Employee Benefits Articles | OHC Benefits",
    description:
      "Plain-language guides on group health, renewals, PEOs, HSAs and Texas open enrollment, written by a licensed agent in The Woodlands, TX.",
  },
  "/benefits-decoded": {
    title: "Benefits Decoded: Health Insurance Explained | OHC Benefits",
    description:
      "A plain-English series on how health plans, networks and costs really work, for Texas families and employers. From OneHarvestCo. Benefits.",
  },
  "/faq": {
    title: "Health Insurance & Group Benefits FAQ | OHC Benefits",
    description:
      "Answers on group health, PEOs, ACA coverage and how I'm paid (carriers pay my commission) from a licensed Texas agent in The Woodlands.",
  },
  "/contact": {
    title: "Contact Kyle Walker | Benefits Consultation in The Woodlands, TX",
    description:
      "Call (713) 725-7413 or send a note to book a consultation on group health, PEO or individual coverage. Serving The Woodlands, Houston and surrounding communities.",
  },
  "/plan-check": {
    title: "Group Health Plan Check for Employers | The Woodlands, TX",
    description:
      "Answer a few quick questions to see which group health funding options, from traditional plans to level-funded, PEO and ICHRA, may fit your company.",
  },
  "/group-benefits-the-woodlands": {
    title: "Group Health Insurance Broker in The Woodlands, TX",
    description:
      "Group health plans for employers in The Woodlands, Houston and surrounding communities. Jan 1 renewals, broker changes and one local owner to call.",
    ogType: "website",
  },
};

export const NOT_FOUND_SEO = {
  title: "Page Not Found | OHC Benefits",
  description: HOME_DESCRIPTION,
  robots: "noindex, follow",
  canonical: false,
};

/** Trim to `max` characters on a word boundary. */
export function trimToWords(text, max = 155) {
  const clean = (text || "").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
}

function upsertMeta(attr, key, content) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(href) {
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (href === null) {
    if (tag) tag.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

/** One pass over every head tag this site publishes. */
export function applySeo({ title, description, path, robots = "index, follow", ogType = "website",
                           canonical = true }) {
  const url = path === "/" ? `${SITE}/` : `${SITE}${path.replace(/\/+$/, "")}`;
  document.title = title;
  upsertMeta("name", "description", description);
  upsertCanonical(canonical ? url : null);
  upsertMeta("name", "robots", robots);

  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:type", ogType);
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:image", OG_IMAGE);
  upsertMeta("property", "og:locale", "en_US");

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", OG_IMAGE);
  return url;
}

/** Article JSON-LD, mounted only while an article is on screen. */
export function setArticleJsonLd(article, url) {
  let tag = document.getElementById("ld-article");
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = "ld-article";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    ...(article.isoDate ? { datePublished: article.isoDate, dateModified: article.isoDate } : {}),
    author: { "@id": `${SITE}/about#kyle`, "@type": "Person", name: "Kyle Walker" },
    publisher: { "@id": `${SITE}/#agency` },
    mainEntityOfPage: url,
    image: OG_IMAGE,
  });
}

export function clearArticleJsonLd() {
  const tag = document.getElementById("ld-article");
  if (tag) tag.remove();
}

/** Page-level JSON-LD graphs, keyed by route. Mounted only while that route is on screen. */
export const PAGE_JSON_LD = {
  "/group-benefits-the-woodlands": {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/group-benefits-the-woodlands#webpage`,
        url: `${SITE}/group-benefits-the-woodlands`,
        name: "Group Health Insurance Broker in The Woodlands, TX",
        description:
          "Group health plans for employers in The Woodlands, Houston and surrounding communities. Jan 1 renewals, broker changes and one local owner to call.",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#agency` },
        breadcrumb: { "@id": `${SITE}/group-benefits-the-woodlands#breadcrumb` },
      },
      {
        "@type": "Service",
        "@id": `${SITE}/group-benefits-the-woodlands#service`,
        name: "Group health insurance and employee benefits guidance",
        serviceType: "Group health insurance brokerage",
        provider: { "@id": `${SITE}/#agency` },
        areaServed: [
          { "@type": "City", name: "The Woodlands, TX" },
          { "@type": "City", name: "Houston, TX" },
          { "@type": "AdministrativeArea", name: "Montgomery County, TX" },
          { "@type": "AdministrativeArea", name: "Harris County, TX" },
        ],
        audience: { "@type": "BusinessAudience", name: "Employers with 10 to 150 employees" },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${SITE}/group-benefits-the-woodlands#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Employers", item: `${SITE}/employers` },
          {
            "@type": "ListItem",
            position: 3,
            name: "Group Benefits in The Woodlands",
            item: `${SITE}/group-benefits-the-woodlands`,
          },
        ],
      },
    ],
  },
};

export function setPageJsonLd(graph) {
  let tag = document.getElementById("ld-page");
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = "ld-page";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(graph);
}

export function clearPageJsonLd() {
  const tag = document.getElementById("ld-page");
  if (tag) tag.remove();
}
